import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, EMPLOYMENT_TYPES, ARRANGEMENTS, SENIORITY } from "@/lib/jobFields";

const cls = "bg-[#F4F4F5] border-black/10 rounded-none";

// Reusable rich job posting fieldset. `values` holds the job fields, `setValues` sets them.
export function JobFields({ values, setValues, pdf, setPdf, titleKey = "title" }) {
  const v = (k) => (e) => setValues({ ...values, [k]: e.target.value });
  const sel = (k) => (val) => setValues({ ...values, [k]: val });
  return (
    <>
      <div className="space-y-2"><Label>Title</Label><Input data-testid="job-title-input" value={values[titleKey]} onChange={v(titleKey)} required placeholder="e.g. VP of Engineering" className={cls} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={values.role_category} onValueChange={sel("role_category")}>
            <SelectTrigger data-testid="job-category" className={`${cls} capitalize`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">{CATEGORIES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Seniority</Label>
          <Select value={values.seniority} onValueChange={sel("seniority")}>
            <SelectTrigger data-testid="job-seniority" className={cls}><SelectValue placeholder="Select level" /></SelectTrigger>
            <SelectContent className="bg-white border-black/10 max-h-64">{SENIORITY.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Employment type</Label>
          <Select value={values.employment_type} onValueChange={sel("employment_type")}>
            <SelectTrigger data-testid="job-employment" className={cls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">{EMPLOYMENT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Work arrangement</Label>
          <Select value={values.work_arrangement} onValueChange={sel("work_arrangement")}>
            <SelectTrigger data-testid="job-arrangement" className={cls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">{ARRANGEMENTS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Location</Label><Input data-testid="job-location" value={values.location} onChange={v("location")} placeholder="SF · NYC · Remote (US)" className={cls} /></div>
        <div className="space-y-2"><Label>Compensation</Label><Input data-testid="job-comp" value={values.compensation_range} onChange={v("compensation_range")} placeholder="$200k–$260k + equity" className={cls} /></div>
      </div>
      <div className="space-y-2"><Label>Overview / description</Label><Textarea data-testid="job-description" value={values.description} onChange={v("description")} placeholder="Company context, the opportunity, why this role matters…" className={`${cls} min-h-[80px]`} /></div>
      <div className="space-y-2"><Label>Key responsibilities</Label><Textarea data-testid="job-responsibilities" value={values.responsibilities} onChange={v("responsibilities")} placeholder="What this person will own and drive…" className={`${cls} min-h-[70px]`} /></div>
      <div className="space-y-2"><Label>Requirements / must-haves</Label><Textarea data-testid="job-requirements" value={values.requirements} onChange={v("requirements")} placeholder="Experience, skills and background required…" className={`${cls} min-h-[70px]`} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Application deadline <span className="text-muted-foreground font-normal">(optional)</span></Label><Input type="date" data-testid="job-deadline" value={values.application_deadline} onChange={v("application_deadline")} className={cls} /></div>
        <div className="space-y-2">
          <Label>How to apply</Label>
          <Select value={values.apply_method} onValueChange={sel("apply_method")}>
            <SelectTrigger data-testid="job-apply-method" className={cls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-black/10">
              <SelectItem value="portal">Signal interest via portal</SelectItem>
              <SelectItem value="external">External link / email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {values.apply_method === "external" && (
        <div className="space-y-2"><Label>Application link or email</Label><Input data-testid="job-apply-link" value={values.apply_link} onChange={v("apply_link")} placeholder="https://… or jobs@company.com" className={cls} /></div>
      )}
      <div className="space-y-2">
        <Label>Attach job description <span className="text-muted-foreground font-normal">(optional PDF)</span></Label>
        <input type="file" accept=".pdf" data-testid="job-pdf"
          onChange={(e) => setPdf(e.target.files?.[0] || null)}
          className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border file:border-black/10 file:bg-[#F4F4F5] file:text-sm file:cursor-pointer" />
        {pdf && <p className="text-xs text-muted-foreground">Selected: {pdf.name}</p>}
      </div>
    </>
  );
}
